/**
 * WALIDATOR GRAFU SCENY (schemaVersion 3) — dane zdalne → model gotowy do rysowania.
 *
 * Polityka (OWNER 2026-10-08, pkt 16/24/25):
 *   · nieznana WŁAŚCIWOŚĆ — ignorowana (rozszerzenia zgodne wstecz nie łamią
 *     starszych aplikacji); nic z niej nie trafia do wyniku;
 *   · nieznany TYP warstwy / efekt / region / zła wartość wyliczenia — cały
 *     motyw odrzucony (zwykłe UI), CHYBA ŻE warstwa ma `optional: true` —
 *     wtedy pomijamy tę jedną warstwę (przyszła skórka z nowym efektem nadal
 *     pokaże resztę na starszej aplikacji);
 *   · liczby spoza zakresu — PRZYCINANE (literówka w panelu = spokojniejsza
 *     animacja, a nie zniknięcie skórki), zły TYP liczby — odrzucenie;
 *   · przekroczony limit (warstwy, cząstki, klatki, punkty) — odrzucenie.
 * Wszystkie adresy pochodzą z listy `assets`, już zwalidowanej (pochodzenie,
 * format, rozmiar) — warstwa wskazuje zasób identyfikatorem, nigdy adresem.
 */

import { Zasob } from './kontrakt';
import {
  Dlugosc, DOPASOWANIA, Dopasowanie, EASINGI, Easing, Efekt, EFEKTY, Font, KlatkaKluczowa, KOTWICE, KROJE,
  LIMITY_SCENY, MIESZANIA, Mieszanie, PunktSciezki, Region, REGIONY, REGIONY_W_PRZEPLYWIE, RodzajEfektu,
  RuchOgraniczony, Scena, ScenaRegionu, Tlo, TRYBY_POZYCJI, TRYBY_RUCHU_OGRANICZONEGO, TrybPozycji, TYPY_WARSTW,
  Warstwa, WarstwaBaza,
} from './scena.model';

class BladSceny extends Error {}

/** Warstwa „opcjonalna" z nieznanym typem/efektem — pomijamy ją po cichu. */
class WarstwaDoPominiecia extends Error {}

type Obj = Record<string, unknown>;
const jestObj = (x: unknown): x is Obj => typeof x === 'object' && x !== null && !Array.isArray(x);

export interface KontekstSceny {
  przyciete: number;
  powody: string[];
}

// ── PRYMITYWY ──────────────────────────────────────────────────────────────

function liczba(x: unknown, min: number, max: number, dom: number, ctx: KontekstSceny, nazwa: string): number {
  if (x === undefined || x === null) return dom;
  if (typeof x !== 'number' || !Number.isFinite(x)) throw new BladSceny(nazwa + ' nie jest liczbą');
  if (x < min || x > max) {
    ctx.przyciete++;
    ctx.powody.push(nazwa + ' ' + x + ' poza [' + min + ', ' + max + '] — przycięte');
    return Math.min(max, Math.max(min, x));
  }
  return x;
}

/** Para liczb. `przedzial` — [od, do] porządkowane rosnąco; inaczej punkt [x, y] bez zmian. */
function para(x: unknown, min: number, max: number, dom: readonly [number, number], ctx: KontekstSceny, nazwa: string, przedzial = true): [number, number] {
  if (x === undefined || x === null) return [dom[0], dom[1]];
  if (!Array.isArray(x) || x.length !== 2) throw new BladSceny(nazwa + ' nie jest parą [a, b]');
  const a = liczba(x[0], min, max, dom[0], ctx, nazwa);
  const b = liczba(x[1], min, max, dom[1], ctx, nazwa);
  return !przedzial || a <= b ? [a, b] : [b, a];
}

function wyliczenie<T extends string>(x: unknown, lista: readonly T[], dom: T, nazwa: string, wielkie = true): T {
  if (x === undefined || x === null) return dom;
  if (typeof x !== 'string') throw new BladSceny(nazwa + ' nie jest tekstem');
  const v = (wielkie ? x.toUpperCase() : x) as T;
  if (!lista.includes(v)) throw new WarstwaNieznanaWartosc(nazwa + ': nieznana wartość ' + x);
  return v;
}

/** Nieznana wartość wyliczenia — odrzuca motyw, a w warstwie `optional` — tylko warstwę. */
class WarstwaNieznanaWartosc extends BladSceny {}

function logiczna(x: unknown, dom: boolean, nazwa: string): boolean {
  if (x === undefined || x === null) return dom;
  if (typeof x !== 'boolean') throw new BladSceny(nazwa + ' nie jest true/false');
  return x;
}

function tekstKrotki(x: unknown, max: number, nazwa: string): string | null {
  if (x === undefined || x === null) return null;
  if (typeof x !== 'string') throw new BladSceny(nazwa + ' nie jest tekstem');
  // eslint-disable-next-line no-control-regex
  const czysty = x.replace(/[\u0000-\u0008\u000b-\u001f\u007f]/g, '');
  return [...czysty].slice(0, max).join('');
}

/**
 * Kolor: `#rrggbb`, `#rrggbbaa`, `rgb(r,g,b)` albo `rgba(r,g,b,a)` → postać
 * zbudowana przez aplikację (`rgba(…)` z liczb). Nazwy, `url()`, zmienne — nie.
 */
export function kolorSceny(x: unknown, nazwa: string): string {
  if (typeof x !== 'string') throw new BladSceny(nazwa + ' nie jest kolorem');
  const s = x.trim().toLowerCase();
  let m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/.exec(s);
  if (m) {
    const r = parseInt(m[1].slice(0, 2), 16);
    const g = parseInt(m[1].slice(2, 4), 16);
    const b = parseInt(m[1].slice(4, 6), 16);
    const a = m[2] ? Math.round((parseInt(m[2], 16) / 255) * 1000) / 1000 : 1;
    return `rgba(${r},${g},${b},${a})`;
  }
  m = /^rgba?\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*(0|1|0?\.\d+))?\s*\)$/.exec(s);
  if (m) {
    const [r, g, b] = [m[1], m[2], m[3]].map((v) => Math.min(255, Number(v)));
    const a = m[4] !== undefined ? Math.min(1, Number(m[4])) : 1;
    return `rgba(${r},${g},${b},${a})`;
  }
  throw new BladSceny(nazwa + ': kolor spoza dozwolonych postaci (' + x + ')');
}

/**
 * Długość: liczba = px; tekst `12px`, `50%`, `0.5n` (znormalizowana 0..1 → %),
 * `10vw`, `10vh`. Zakres wartości przycinany (−5000..5000 px, −500..500 %).
 */
export function dlugosc(x: unknown, nazwa: string, ctx: KontekstSceny): Dlugosc | null {
  if (x === undefined || x === null) return null;
  if (typeof x === 'number') {
    return { v: liczba(x, -5000, 5000, 0, ctx, nazwa), u: 'px' };
  }
  if (typeof x !== 'string') throw new BladSceny(nazwa + ' nie jest długością');
  const m = /^\s*(-?\d+(?:\.\d+)?)\s*(px|%|n|vw|vh)?\s*$/.exec(x);
  if (!m) throw new BladSceny(nazwa + ': długość spoza dozwolonych postaci (' + x + ')');
  const v = Number(m[1]);
  const u = m[2] ?? 'px';
  if (u === 'n') return { v: liczba(v * 100, -500, 500, 0, ctx, nazwa), u: '%' };
  if (u === '%') return { v: liczba(v, -500, 500, 0, ctx, nazwa), u: '%' };
  if (u === 'vw' || u === 'vh') return { v: liczba(v, -200, 200, 0, ctx, nazwa), u };
  return { v: liczba(v, -5000, 5000, 0, ctx, nazwa), u: 'px' };
}

// ── REGIONY ────────────────────────────────────────────────────────────────

const ALIASY_REGIONOW: Readonly<Record<string, Region>> = {
  header: 'APP_HEADER', logo: 'LOGO', messagebar: 'MESSAGE_BAR', message_bar: 'MESSAGE_BAR', hero: 'EVENT_HERO',
  prerestaurants: 'PRE_RESTAURANTS', pre_restaurants: 'PRE_RESTAURANTS', content: 'CONTENT_BACKGROUND',
  contentbackground: 'CONTENT_BACKGROUND', contentforeground: 'CONTENT_FOREGROUND', sectionheader: 'SECTION_HEADER',
  postcontent: 'POST_CONTENT', footer: 'FOOTER', overlay: 'FULL_SCREEN_OVERLAY', screen: 'FULL_SCREEN_OVERLAY',
  emptystate: 'EMPTY_STATE', bottomnav: 'BOTTOM_NAV',
};

export function region(x: unknown, nazwa: string): Region {
  if (typeof x !== 'string') throw new BladSceny(nazwa + ' nie jest nazwą regionu');
  const w = x.trim();
  if ((REGIONY as readonly string[]).includes(w.toUpperCase())) return w.toUpperCase() as Region;
  const kotwica = KOTWICE[w.toUpperCase()];
  if (kotwica) return kotwica;
  const alias = ALIASY_REGIONOW[w.toLowerCase().replace(/[-\s]/g, '')] ?? ALIASY_REGIONOW[w.toLowerCase()];
  if (alias) return alias;
  throw new WarstwaNieznanaWartosc(nazwa + ': nieznany region ' + x);
}

// ── TŁO ────────────────────────────────────────────────────────────────────

function tlo(x: unknown, zasoby: ReadonlyMap<string, Zasob>, ctx: KontekstSceny, nazwa: string): Tlo | null {
  if (x === undefined || x === null) return null;
  if (!jestObj(x)) throw new BladSceny(nazwa + ' nie jest obiektem');
  const typ = wyliczenie(x['type'], ['SOLID', 'GRADIENT', 'IMAGE'] as const, 'SOLID', nazwa + '.type');
  const przystanki: { kolor: string; at: number }[] = [];
  if (typ === 'GRADIENT') {
    const st = x['stops'];
    if (!Array.isArray(st) || st.length < 2 || st.length > 8) throw new BladSceny(nazwa + '.stops: 2–8 przystanków');
    st.forEach((p, i) => {
      if (!jestObj(p)) throw new BladSceny(nazwa + '.stops[' + i + '] nie jest obiektem');
      przystanki.push({ kolor: kolorSceny(p['color'], nazwa + '.stops.color'), at: liczba(p['at'], 0, 1, i / (st.length - 1), ctx, nazwa + '.stops.at') });
    });
  }
  let url: string | null = null;
  if (typ === 'IMAGE') {
    url = zasobUrl(x['asset'], zasoby, nazwa + '.asset');
  }
  return {
    typ,
    kolor: typ === 'SOLID' ? kolorSceny(x['color'], nazwa + '.color') : null,
    kat: liczba(x['angle'], -360, 360, 180, ctx, nazwa + '.angle'),
    przystanki,
    url,
    dopasowanie: wyliczenie(x['fit'], ['COVER', 'CONTAIN', 'REPEAT', 'STRETCH'] as const, 'COVER', nazwa + '.fit'),
    pozycja: para(x['position'], 0, 1, [0.5, 0.5], ctx, nazwa + '.position', false),
    krycie: liczba(x['opacity'], 0, 1, 1, ctx, nazwa + '.opacity'),
    mieszanie: wyliczenie(x['blendMode'], MIESZANIA, 'normal' as Mieszanie, nazwa + '.blendMode', false),
  };
}

function zasobUrl(x: unknown, zasoby: ReadonlyMap<string, Zasob>, nazwa: string): string {
  if (typeof x !== 'string' || !x) throw new BladSceny(nazwa + ': brak identyfikatora zasobu');
  const z = zasoby.get(x);
  if (!z || !z.url) throw new BladSceny(nazwa + ': zasób „' + x + '" nie istnieje w assets');
  return z.url;
}

// ── EFEKTY ─────────────────────────────────────────────────────────────────

/** Domyślne parametry efektu — rozsądne, spokojne (ozdoba, nie reklama). */
const DOMYSLNE: Readonly<Partial<Record<RodzajEfektu, { czas: number; a?: number }>>> = {
  FLOAT: { czas: 3200, a: 6 }, BOB: { czas: 1800, a: 3 }, SWAY: { czas: 4000, a: 4 }, PULSE: { czas: 2400 },
  ROTATE: { czas: 4000 }, SPIN: { czas: 12000 }, FADE: { czas: 3000 }, BLINK: { czas: 2400 }, SHIMMER: { czas: 2400 },
  GLOW: { czas: 2800 }, BOUNCE: { czas: 2400, a: 4 }, FLY: { czas: 6000 }, DRIFT: { czas: 6000, a: 4 },
  FALL: { czas: 5000 }, RISE: { czas: 5000 }, ZOOM: { czas: 3000 }, WIGGLE: { czas: 900, a: 6 },
  SHAKE_LIGHT: { czas: 700, a: 2 }, ORBIT: { czas: 6000 }, PATH: { czas: 6000 }, KEYFRAMES: { czas: 3000 },
  PARALLAX: { czas: 1000, a: 0.2 }, SPARKLE: { czas: 2600 },
};

function efekt(x: unknown, ctx: KontekstSceny, nazwa: string): Efekt {
  if (!jestObj(x)) throw new BladSceny(nazwa + ' nie jest obiektem');
  const typ = wyliczenie(x['type'] ?? x['effect'], EFEKTY, 'FLOAT', nazwa + '.type');
  const dom = DOMYSLNE[typ] ?? { czas: 3000 };
  const powt = x['repeat'];
  let powtorzenia = Infinity;
  if (powt !== undefined && powt !== null && powt !== 'infinite') {
    powtorzenia = Math.round(liczba(powt, 1, 1000, 1, ctx, nazwa + '.repeat'));
  }
  const klatki: KlatkaKluczowa[] = [];
  if (x['keyframes'] !== undefined) {
    const k = x['keyframes'];
    if (!Array.isArray(k) || k.length > LIMITY_SCENY.klatek) throw new BladSceny(nazwa + '.keyframes: lista do ' + LIMITY_SCENY.klatek);
    k.forEach((kl, i) => {
      if (!jestObj(kl)) throw new BladSceny(nazwa + '.keyframes[' + i + '] nie jest obiektem');
      // WYŁĄCZNIE transform (x, y, rotate, scale) i opacity — żadnego CSS.
      klatki.push({
        t: liczba(kl['t'], 0, 1, i / Math.max(1, k.length - 1), ctx, nazwa + '.t'),
        x: kl['x'] === undefined ? undefined : liczba(kl['x'], -2000, 2000, 0, ctx, nazwa + '.x'),
        y: kl['y'] === undefined ? undefined : liczba(kl['y'], -2000, 2000, 0, ctx, nazwa + '.y'),
        rotate: kl['rotate'] === undefined ? undefined : liczba(kl['rotate'], -3600, 3600, 0, ctx, nazwa + '.rotate'),
        scale: kl['scale'] === undefined ? undefined : liczba(kl['scale'], 0, 10, 1, ctx, nazwa + '.scale'),
        opacity: kl['opacity'] === undefined ? undefined : liczba(kl['opacity'], 0, 1, 1, ctx, nazwa + '.opacity'),
      });
    });
    klatki.sort((a, b) => a.t - b.t);
  }
  const sciezka: PunktSciezki[] = [];
  if (x['path'] !== undefined) {
    const p = x['path'];
    if (!Array.isArray(p) || p.length > LIMITY_SCENY.punktowSciezki) throw new BladSceny(nazwa + '.path: lista do ' + LIMITY_SCENY.punktowSciezki);
    p.forEach((pt, i) => {
      if (!jestObj(pt)) throw new BladSceny(nazwa + '.path[' + i + '] nie jest obiektem');
      sciezka.push({
        t: liczba(pt['t'], 0, 1, i / Math.max(1, p.length - 1), ctx, nazwa + '.t'),
        x: liczba(pt['x'], -1, 2, 0, ctx, nazwa + '.x'),
        y: liczba(pt['y'], -1, 2, 0, ctx, nazwa + '.y'),
      });
    });
    sciezka.sort((a, b) => a.t - b.t);
  }
  if ((typ === 'PATH' && sciezka.length < 2) || (typ === 'KEYFRAMES' && klatki.length < 2)) {
    throw new BladSceny(nazwa + ': ' + typ + ' wymaga co najmniej 2 punktów');
  }
  const krycie = para(x['opacityRange'] ?? x['opacity'], 0, 1, [0.35, 1], ctx, nazwa + '.opacityRange');
  const skala = para(x['scaleRange'] ?? x['scale'], 0.1, 4, [1, typ === 'ZOOM' ? 1.15 : 1.08], ctx, nazwa + '.scaleRange');
  return {
    typ,
    czas: liczba(x['duration'], 120, 120000, dom.czas, ctx, nazwa + '.duration'),
    opoznienie: liczba(x['delay'], 0, 60000, 0, ctx, nazwa + '.delay'),
    przerwa: liczba(x['interval'], 0, 120000, 0, ctx, nazwa + '.interval'),
    powtorzenia,
    faza: liczba(x['phase'], 0, 1, 0, ctx, nazwa + '.phase'),
    easing: wyliczenie(x['easing'], EASINGI, (typ === 'PATH' || typ === 'KEYFRAMES' ? 'linear' : 'ease-in-out') as Easing, nazwa + '.easing', false),
    amplituda: liczba(x['amplitude'], typ === 'PARALLAX' ? -1 : 0, typ === 'PARALLAX' ? 1 : 400, dom.a ?? 6, ctx, nazwa + '.amplitude'),
    dystans: liczba(x['distance'], 0, 3000, 120, ctx, nazwa + '.distance'),
    kierunek: liczba(x['direction'], -360, 360, 0, ctx, nazwa + '.direction'),
    obrot: liczba(x['rotation'], -3600, 3600, typ === 'ROTATE' ? 15 : typ === 'SPIN' ? 360 : 0, ctx, nazwa + '.rotation'),
    krycie: [krycie[0], krycie[1]],
    skala: [skala[0], skala[1]],
    os: para(x['origin'], 0, 1, typ === 'SWAY' ? [0.5, 0] : [0.5, 0.5], ctx, nazwa + '.origin', false),
    naprzemiennie: logiczna(x['alternate'], false, nazwa + '.alternate'),
    kolor: x['color'] === undefined ? null : kolorSceny(x['color'], nazwa + '.color'),
    promien: liczba(x['radius'], 0, 400, typ === 'GLOW' ? 8 : typ === 'ORBIT' ? 10 : 6, ctx, nazwa + '.radius'),
    ile: Math.round(liczba(x['count'], 1, 12, 4, ctx, nazwa + '.count')),
    klatki,
    sciezka,
    obracajWzdluz: logiczna(x['orient'], false, nazwa + '.orient'),
  };
}

// ── WARSTWY ────────────────────────────────────────────────────────────────

interface Zrodla {
  readonly zasoby: ReadonlyMap<string, Zasob>;
  readonly ctx: KontekstSceny;
}

function font(x: unknown, z: Zrodla, nazwa: string): Font {
  const f = jestObj(x) ? x : {};
  const rodzinaSurowa = f['family'] ?? f['fontFamily'];
  let rodzina = KROJE['Plus Jakarta Sans'];
  if (rodzinaSurowa !== undefined) {
    if (typeof rodzinaSurowa !== 'string' || !KROJE[rodzinaSurowa]) {
      throw new WarstwaNieznanaWartosc(nazwa + '.family: krój spoza allowlisty (' + String(rodzinaSurowa) + ')');
    }
    rodzina = KROJE[rodzinaSurowa];
  }
  return {
    rodzina,
    // Krój marki ma odcienie 400–800 (bez syntetycznego pogrubienia ponad plik).
    waga: Math.round(liczba(f['weight'] ?? f['fontWeight'], 400, 800, 700, z.ctx, nazwa + '.weight') / 100) * 100,
    rozmiar: liczba(f['size'] ?? f['fontSize'], 6, 120, 16, z.ctx, nazwa + '.size'),
    interlinia: liczba(f['lineHeight'], 0.7, 3, 1.2, z.ctx, nazwa + '.lineHeight'),
    odstep: liczba(f['letterSpacing'], -4, 20, 0, z.ctx, nazwa + '.letterSpacing'),
    wersaliki: logiczna(f['uppercase'], false, nazwa + '.uppercase'),
    kursywa: logiczna(f['italic'], false, nazwa + '.italic'),
  };
}

let licznikWarstw = 0;

function warstwa(x: unknown, regionDomyslny: Region | null, z: Zrodla, nazwa: string, wariant = false): Warstwa {
  if (!jestObj(x)) throw new BladSceny(nazwa + ' nie jest obiektem');
  const ctx = z.ctx;
  const typ = wyliczenie(x['type'], TYPY_WARSTW, 'IMAGE', nazwa + '.type');
  const regionSurowy = x['region'] ?? x['anchor'];
  const reg = regionSurowy !== undefined ? region(regionSurowy, nazwa + '.region') : regionDomyslny;
  if (!reg) throw new BladSceny(nazwa + ': warstwa bez regionu');
  const tryb: TrybPozycji = wyliczenie(x['positionMode'], TRYBY_POZYCJI, 'ABSOLUTE', nazwa + '.positionMode');
  if (tryb === 'FLOW' && !REGIONY_W_PRZEPLYWIE.includes(reg)) {
    throw new BladSceny(nazwa + ': FLOW tylko w regionach w przepływie (' + REGIONY_W_PRZEPLYWIE.join(', ') + ')');
  }
  if (tryb === 'FIXED' && reg !== 'FULL_SCREEN_OVERLAY') {
    throw new BladSceny(nazwa + ': FIXED tylko w FULL_SCREEN_OVERLAY');
  }
  const efektySurowe = x['animations'] ?? x['effects'];
  if (efektySurowe !== undefined && !Array.isArray(efektySurowe)) throw new BladSceny(nazwa + '.animations nie jest listą');
  const listaEf = (efektySurowe as unknown[] | undefined) ?? [];
  if (listaEf.length > LIMITY_SCENY.efektowWarstwy) throw new BladSceny(nazwa + ': efektów > ' + LIMITY_SCENY.efektowWarstwy);
  const efekty = listaEf.map((e, i) => efekt(e, ctx, nazwa + '.animations[' + i + ']'));

  const staticAsset = x['staticAsset'];
  let urlStatyczny: string | null = null;
  if (staticAsset !== undefined) urlStatyczny = zasobUrl(staticAsset, z.zasoby, nazwa + '.staticAsset');

  const baza: Omit<WarstwaBaza, 'typ'> = {
    id: tekstKrotki(x['id'], 40, nazwa + '.id') ?? 'w' + ++licznikWarstw,
    region: reg,
    tryb,
    x: dlugosc(x['x'], nazwa + '.x', ctx) ?? { v: 0, u: 'px' },
    y: dlugosc(x['y'], nazwa + '.y', ctx) ?? { v: 0, u: 'px' },
    dx: liczba(x['dx'], -2000, 2000, 0, ctx, nazwa + '.dx'),
    dy: liczba(x['dy'], -2000, 2000, 0, ctx, nazwa + '.dy'),
    szer: dlugosc(x['width'], nazwa + '.width', ctx),
    wys: dlugosc(x['height'], nazwa + '.height', ctx),
    maxSzer: x['maxWidth'] === undefined ? null : liczba(x['maxWidth'], 1, 4000, 4000, ctx, nazwa + '.maxWidth'),
    maxWys: x['maxHeight'] === undefined ? null : liczba(x['maxHeight'], 1, 4000, 4000, ctx, nazwa + '.maxHeight'),
    poczatek: para(x['origin'], 0, 1, [0, 0], ctx, nazwa + '.origin', false),
    obrot: liczba(x['rotation'], -360, 360, 0, ctx, nazwa + '.rotation'),
    skala: liczba(x['scale'], 0.05, 10, 1, ctx, nazwa + '.scale'),
    lustro: wyliczenie(x['mirror'], ['NONE', 'X', 'Y'] as const, 'NONE', nazwa + '.mirror'),
    krycie: liczba(x['opacity'], 0, 1, 1, ctx, nazwa + '.opacity'),
    z: Math.round(liczba(x['zIndex'], -20, 20, 0, ctx, nazwa + '.zIndex')),
    efekty,
    ruchOgraniczony: wyliczenie(x['reducedMotion'], TRYBY_RUCHU_OGRANICZONEGO, 'FIRST_FRAME' as RuchOgraniczony, nazwa + '.reducedMotion'),
    urlStatyczny,
    minEkran: x['minScreenWidth'] === undefined ? null : liczba(x['minScreenWidth'], 0, 4000, 0, ctx, nazwa + '.minScreenWidth'),
    maxEkran: x['maxScreenWidth'] === undefined ? null : liczba(x['maxScreenWidth'], 0, 4000, 4000, ctx, nazwa + '.maxScreenWidth'),
  };

  let wynik: Warstwa;
  if (typ === 'IMAGE') {
    const idZasobu = x['asset'];
    const url = zasobUrl(idZasobu, z.zasoby, nazwa + '.asset');
    const zasob = z.zasoby.get(idZasobu as string)!;
    const animowany = zasob.rodzaj === 'LIGHTWEIGHT_VECTOR_ANIMATION' || zasob.rodzaj === 'ANIMATED_IMAGE';
    const statyczny = baza.urlStatyczny ?? zasob.urlStatyczny ?? null;
    let epizod: [number, number] | null = null;
    const graj = x['play'];
    if (jestObj(graj)) {
      epizod = [liczba(graj['episodeMs'], 1000, 120000, 6000, ctx, nazwa + '.play.episodeMs'),
        liczba(graj['restMs'], 0, 600000, 18000, ctx, nazwa + '.play.restMs')];
    } else if (graj !== undefined && graj !== 'CONTINUOUS') {
      throw new WarstwaNieznanaWartosc(nazwa + '.play: nieznana wartość');
    } else if (animowany && statyczny && graj === undefined) {
      // Animowany plik w <img> przerysowuje się w każdej klatce — domyślnie
      // epizody (6 s ruchu / 18 s spoczynku na pliku statycznym).
      epizod = [6000, 18000];
    }
    // Pliku, który sam się animuje, nie da się „zatrzymać na pierwszej klatce" —
    // ograniczony ruch pokazuje plik statyczny, a bez niego warstwa znika.
    let ruch = baza.ruchOgraniczony;
    if (animowany && ruch === 'FIRST_FRAME') ruch = statyczny ? 'STATIC_ASSET' : 'HIDE';
    if (ruch === 'STATIC_ASSET' && !statyczny) ruch = 'HIDE';
    wynik = {
      ...baza, typ: 'IMAGE', url, urlStatyczny: statyczny, ruchOgraniczony: ruch,
      dopasowanie: wyliczenie(x['fit'] ?? x['aspectMode'], DOPASOWANIA, 'CONTAIN' as Dopasowanie, nazwa + '.fit'),
      animowanyPlik: animowany, epizod,
    };
  } else if (typ === 'TEXT') {
    const t = tekstKrotki(x['text'], LIMITY_SCENY.znakowTekstu, nazwa + '.text');
    if (!t || !t.trim()) throw new BladSceny(nazwa + ': pusty tekst');
    const cien = x['shadow'];
    wynik = {
      ...baza, typ: 'TEXT', tekst: t,
      font: font(x['font'] ?? x, z, nazwa + '.font'),
      kolor: x['color'] === undefined ? 'rgba(255,255,255,1)' : kolorSceny(x['color'], nazwa + '.color'),
      wyrownanie: wyliczenie(x['align'] ?? x['alignment'], ['left', 'center', 'right'] as const, 'left', nazwa + '.align', false),
      maxLinii: Math.round(liczba(x['maxLines'], 1, 8, 3, ctx, nazwa + '.maxLines')),
      cien: jestObj(cien) ? { kolor: kolorSceny(cien['color'], nazwa + '.shadow.color'), rozmycie: liczba(cien['blur'], 0, 40, 4, ctx, nazwa + '.shadow.blur') } : null,
    };
  } else if (typ === 'SHAPE') {
    const obrys = x['border'];
    const wyp = tlo(x['fill'] ?? { type: 'SOLID', color: '#ffffff' }, z.zasoby, ctx, nazwa + '.fill');
    if (!wyp) throw new BladSceny(nazwa + ': kształt bez wypełnienia');
    if (!baza.szer || !baza.wys) throw new BladSceny(nazwa + ': kształt wymaga width i height');
    wynik = {
      ...baza, typ: 'SHAPE',
      ksztalt: wyliczenie(x['shape'], ['RECT', 'ELLIPSE'] as const, 'RECT', nazwa + '.shape'),
      wypelnienie: wyp,
      zaokraglenie: liczba(x['radius'], 0, 2000, 0, ctx, nazwa + '.radius'),
      obrys: jestObj(obrys) ? { kolor: kolorSceny(obrys['color'], nazwa + '.border.color'), grubosc: liczba(obrys['width'], 0, 40, 1, ctx, nazwa + '.border.width') } : null,
    };
  } else {
    const ile = Math.round(liczba(x['count'], 1, LIMITY_SCENY.czastekPola, 8, ctx, nazwa + '.count'));
    const obszarSurowy = x['spawnRegion'] ?? x['spawn'];
    let obszar: [number, number, number, number] = [0, -0.05, 1, 0.05];
    if (obszarSurowy !== undefined) {
      if (!Array.isArray(obszarSurowy) || obszarSurowy.length !== 4) throw new BladSceny(nazwa + '.spawnRegion: [x, y, w, h]');
      obszar = obszarSurowy.map((v, i) => liczba(v, -1, 2, 0, ctx, nazwa + '.spawnRegion[' + i + ']')) as [number, number, number, number];
    }
    const kierunekSurowy = x['direction'];
    let kierunek = 90;
    if (typeof kierunekSurowy === 'string') {
      const k = ({ DOWN: 90, UP: 270, LEFT: 180, RIGHT: 0 } as Record<string, number>)[kierunekSurowy.toUpperCase()];
      if (k === undefined) throw new WarstwaNieznanaWartosc(nazwa + '.direction: nieznany kierunek');
      kierunek = k;
    } else if (kierunekSurowy !== undefined) {
      kierunek = liczba(kierunekSurowy, -360, 360, 90, ctx, nazwa + '.direction');
    }
    wynik = {
      ...baza, typ: 'PARTICLES',
      url: zasobUrl(x['asset'], z.zasoby, nazwa + '.asset'),
      ile, obszar, kierunek,
      predkosc: para([x['speedMin'] ?? 20, x['speedMax'] ?? 40], 1, 2000, [20, 40], ctx, nazwa + '.speed'),
      droga: x['distance'] === undefined ? null : liczba(x['distance'], 1, 4000, 200, ctx, nazwa + '.distance'),
      obrotCzastki: para([x['rotationMin'] ?? 0, x['rotationMax'] ?? 0], -3600, 3600, [0, 0], ctx, nazwa + '.rotation'),
      skalaCzastki: para([x['scaleMin'] ?? 1, x['scaleMax'] ?? 1], 0.05, 6, [1, 1], ctx, nazwa + '.scale'),
      kryciePrzedzial: para([x['opacityMin'] ?? 1, x['opacityMax'] ?? 1], 0, 1, [1, 1], ctx, nazwa + '.opacity'),
      zycie: para(x['lifetime'] ?? [x['lifetimeMin'] ?? 6000, x['lifetimeMax'] ?? 10000], 200, 120000, [6000, 10000], ctx, nazwa + '.lifetime'),
      opoznienia: para(x['delayRange'] ?? [0, 0], 0, 120000, [0, 0], ctx, nazwa + '.delayRange'),
      dryf: liczba(x['horizontalDrift'], 0, 400, 0, ctx, nazwa + '.horizontalDrift'),
      rozmiar: liczba(x['size'], 2, 400, 16, ctx, nazwa + '.size'),
    };
  }

  // WARIANTY RESPONSYWNE: pierwszy pasujący do szerokości ekranu zastępuje
  // warstwę (bez wykonywania kodu — to tylko druga, też zwalidowana warstwa).
  if (!wariant && x['responsive'] !== undefined) {
    const r = x['responsive'];
    if (!Array.isArray(r) || r.length > 4) throw new BladSceny(nazwa + '.responsive: lista do 4 wariantów');
    const warianty = r.map((w, i) => {
      if (!jestObj(w)) throw new BladSceny(nazwa + '.responsive[' + i + '] nie jest obiektem');
      const { responsive: _pominiete, ...bezWariantow } = x;
      const scalone = { ...bezWariantow, ...w, minScreenWidth: w['minWidth'] ?? w['minScreenWidth'], maxScreenWidth: w['maxWidth'] ?? w['maxScreenWidth'] };
      return warstwa(scalone, reg, z, nazwa + '.responsive[' + i + ']', true);
    });
    return { ...wynik, warianty } as Warstwa;
  }
  return wynik;
}

/** Wynik walidacji sceny. */
export interface WynikSceny {
  readonly scena: Scena | null;
  readonly powod: string | null;
}

/**
 * Graf sceny z konfiguracji (schemaVersion 3). `zasoby` — już zwalidowane.
 * Zwraca `scena: null` z powodem przy KAŻDYM błędzie (motyw w całości odpada).
 */
export function walidujScene(zrodlo: Obj, zasoby: ReadonlyMap<string, Zasob>, ctx: KontekstSceny): WynikSceny {
  try {
    licznikWarstw = 0;
    const z: Zrodla = { zasoby, ctx };
    const regiony: Partial<Record<Region, { tlo: Tlo | null; wysokosc: number | 'FULL' | null; proporcja: number | null; maxWysokosc: number | null; przytnij: boolean; warstwy: Warstwa[] }>> = {};
    const zapewnij = (r: Region) =>
      (regiony[r] ??= { tlo: null, wysokosc: null, proporcja: null, maxWysokosc: null, przytnij: true, warstwy: [] });
    const dodaj = (surowa: unknown, domyslny: Region | null, nazwa: string): void => {
      try {
        const w = warstwa(surowa, domyslny, z, nazwa);
        zapewnij(w.region).warstwy.push(w);
      } catch (e) {
        // Warstwa `optional: true` z nieznaną wartością (nowy efekt, nowy
        // region) — pomijamy ją, reszta skórki zostaje. Każdy inny błąd
        // (zły typ, brak zasobu, limit) odrzuca CAŁY motyw.
        if (e instanceof WarstwaNieznanaWartosc && jestObj(surowa) && surowa['optional'] === true) {
          ctx.powody.push('pominięta warstwa opcjonalna: ' + e.message);
          return;
        }
        throw e;
      }
    };

    const sceny = zrodlo['scenes'] ?? zrodlo['regions'];
    if (sceny !== undefined) {
      if (!jestObj(sceny)) throw new BladSceny('scenes nie jest obiektem');
      for (const [klucz, s] of Object.entries(sceny)) {
        const reg = region(klucz, 'scenes.' + klucz);
        if (!jestObj(s)) throw new BladSceny('scenes.' + klucz + ' nie jest obiektem');
        const r = zapewnij(reg);
        r.tlo = tlo(s['background'], zasoby, ctx, 'scenes.' + klucz + '.background');
        const wys = s['height'];
        if (wys === 'FULL') {
          if (reg !== 'CONTENT_BACKGROUND' && reg !== 'CONTENT_FOREGROUND') throw new BladSceny('height FULL tylko w tle/przodzie treści');
          r.wysokosc = 'FULL';
        } else if (wys !== undefined) {
          const max = REGIONY_W_PRZEPLYWIE.includes(reg) ? LIMITY_SCENY.wysokoscPrzeplywu : LIMITY_SCENY.wysokoscRegionu;
          r.wysokosc = liczba(wys, 0, max, 0, ctx, 'scenes.' + klucz + '.height');
        }
        if (s['aspectRatio'] !== undefined) r.proporcja = liczba(s['aspectRatio'], 0.2, 20, 1, ctx, 'scenes.' + klucz + '.aspectRatio');
        if (s['maxHeight'] !== undefined) r.maxWysokosc = liczba(s['maxHeight'], 1, LIMITY_SCENY.wysokoscRegionu, 480, ctx, 'scenes.' + klucz + '.maxHeight');
        r.przytnij = logiczna(s['clip'], true, 'scenes.' + klucz + '.clip');
        const lw = s['layers'];
        if (lw !== undefined) {
          if (!Array.isArray(lw)) throw new BladSceny('scenes.' + klucz + '.layers nie jest listą');
          lw.forEach((w, i) => dodaj(w, reg, 'scenes.' + klucz + '.layers[' + i + ']'));
        }
      }
    }
    const warstwy = zrodlo['layers'];
    if (warstwy !== undefined) {
      if (!Array.isArray(warstwy)) throw new BladSceny('layers nie jest listą');
      warstwy.forEach((w, i) => dodaj(w, null, 'layers[' + i + ']'));
    }

    // ── LIMITY ─────────────────────────────────────────────────────────────
    let wszystkich = 0;
    let czastek = 0;
    for (const [r, s] of Object.entries(regiony)) {
      if (!s) continue;
      if (s.warstwy.length > LIMITY_SCENY.warstwRegionu) throw new BladSceny('region ' + r + ': warstw > ' + LIMITY_SCENY.warstwRegionu);
      wszystkich += s.warstwy.length;
      for (const w of s.warstwy) if (w.typ === 'PARTICLES') czastek += w.ile;
      // Region w przepływie musi znać swoją wysokość — inaczej skok układu.
      if (REGIONY_W_PRZEPLYWIE.includes(r as Region) && s.wysokosc === null && s.proporcja === null
        && !s.warstwy.some((w) => w.tryb === 'FLOW')) {
        throw new BladSceny('region ' + r + ' w przepływie bez height / aspectRatio / warstwy FLOW');
      }
    }
    if (wszystkich > LIMITY_SCENY.warstw) throw new BladSceny('warstw ' + wszystkich + ' > ' + LIMITY_SCENY.warstw);
    if (czastek > LIMITY_SCENY.czastekRazem) throw new BladSceny('cząstek ' + czastek + ' > ' + LIMITY_SCENY.czastekRazem);

    const adresy = new Set<string>();
    const gotowe: Partial<Record<Region, ScenaRegionu>> = {};
    for (const [r, s] of Object.entries(regiony)) {
      if (!s) continue;
      for (const w of s.warstwy) {
        for (const ww of [w, ...((w as Warstwa & { warianty?: Warstwa[] }).warianty ?? [])]) {
          if (ww.typ === 'IMAGE' || ww.typ === 'PARTICLES') adresy.add(ww.url);
          if (ww.urlStatyczny) adresy.add(ww.urlStatyczny);
          if (ww.typ === 'SHAPE' && ww.wypelnienie.url) adresy.add(ww.wypelnienie.url);
        }
      }
      if (s.tlo?.url) adresy.add(s.tlo.url);
      gotowe[r as Region] = { region: r as Region, ...s, warstwy: [...s.warstwy].sort((a, b) => a.z - b.z) };
    }
    return { scena: { regiony: gotowe, adresy: [...adresy] }, powod: null };
  } catch (e) {
    if (e instanceof BladSceny || e instanceof WarstwaDoPominiecia) {
      return { scena: null, powod: e.message };
    }
    return { scena: null, powod: 'błąd sceny: ' + String((e as Error)?.message ?? e) };
  }
}
